import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { newCondition, stepsOf } from 'features/datasets/components/EvalConditions';
import { useEvalLineages } from 'features/agent-analytics/components/EvalFilterSection';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';
import { loadEvalGraph } from 'shared/components/filters/evals/evalLineCache';

/**
 * The Evals tab of the filter picker: an eval, then what about it — its
 * verdict, its score, its reason code, or what one of its steps answered.
 * Picking emits one eval condition line (features/datasets/components/
 * EvalConditions), which the builder shows as a chip in Filters.
 */

const VERDICT_ITEMS = [
  ['fail', 'Failed'], ['pass', 'Passed'], ['not_fail', 'Did not fail'],
  ['wrong', 'Marked wrong'], ['not_wrong', 'Not marked wrong'], ['right', 'Marked right'],
];

const ROW = 'mx-1.5 flex w-[calc(100%-12px)] items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-[13px] transition-colors hover:bg-surface-hover';
const GROUP = 'px-3 pb-1 pt-3 text-[11px] font-medium text-[color:var(--text-muted)]';

function line(lineageId, patch) {
  return { ...newCondition(), lineage_id: lineageId || '', ...patch };
}

export default function EvalPickerPane({ teamId: teamIdArg, search = '', onPick }) {
  const teamId = getSelectedTeamNumericId() || teamIdArg;
  const lineages = useEvalLineages(teamId);
  const [open, setOpen] = useState(null);        // { id, name } | null
  const [graph, setGraph] = useState(null);
  useEffect(() => {
    setGraph(null);
    if (!open || !open.id) return undefined;
    let off = false;
    loadEvalGraph(teamId, open.id).then((g) => { if (!off) setGraph(g); });
    return () => { off = true; };
  }, [open, teamId]);

  const q = search.trim().toLowerCase();

  if (!open) {
    const list = [{ id: '', name: 'Any eval' }, ...(lineages || []).map((l) => ({ id: l.id, name: l.name }))]
      .filter((l) => !q || l.name.toLowerCase().includes(q));
    return (
      <div data-testid="eval-picker">
        <div className={GROUP}>Evals</div>
        {list.length ? list.map((l) => (
          <button key={l.id || 'any'} type="button" onClick={() => setOpen(l)} className={ROW}>
            <span className="truncate text-[color:var(--text-primary)]">{l.name}</span>
            <ChevronRight size={14} className="text-[color:var(--text-muted)]" />
          </button>
        )) : <div className="px-4 py-6 text-center text-[12px] text-[color:var(--text-muted)]">No matching evals.</div>}
      </div>
    );
  }

  const steps = open.id ? stepsOf(graph) : [];
  const stepFields = steps.flatMap((st) => Object.entries(st.output || {}).map(([name, f]) => ({ node_id: st.id, step: st.name, field: name, type: f?.type || 'string' })))
    .filter((f) => !q || `${f.step} ${f.field}`.toLowerCase().includes(q));
  const verdicts = VERDICT_ITEMS.filter(([, label]) => !q || label.toLowerCase().includes(q));
  return (
    <div data-testid="eval-picker">
      <button type="button" onClick={() => setOpen(null)} className="flex w-full items-center gap-1 border-b border-[color:var(--border-default)] px-3 py-2 text-left text-[12px] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]">
        <ChevronLeft size={14} /> {open.name}
      </button>
      <div className={GROUP}>Verdict</div>
      {verdicts.map(([what, label]) => (
        <button key={what} type="button" onClick={() => onPick(line(open.id, { what }))} className={ROW}>
          <span className="text-[color:var(--text-primary)]">{label}</span>
        </button>
      ))}
      {!q || 'score'.includes(q) ? (
        <button type="button" onClick={() => onPick(line(open.id, { what: 'score', op: 'lt', value: '' }))} className={ROW}>
          <span className="text-[color:var(--text-primary)]">Score</span>
          <span className="text-[11px] text-[color:var(--text-muted)]">number</span>
        </button>
      ) : null}
      {!q || 'reason code'.includes(q) ? (
        <button type="button" onClick={() => onPick(line(open.id, { what: 'reason' }))} className={ROW}>
          <span className="text-[color:var(--text-primary)]">Reason code</span>
          <span className="text-[11px] text-[color:var(--text-muted)]">string</span>
        </button>
      ) : null}
      {open.id ? (
        <>
          <div className={GROUP}>Steps</div>
          {graph == null ? <div className="px-3 py-1.5 text-[12px] text-[color:var(--text-muted)]">Loading steps…</div> : null}
          {graph != null && !stepFields.length ? <div className="px-3 py-1.5 text-[12px] text-[color:var(--text-muted)]">No step answers to filter on.</div> : null}
          {stepFields.map((f) => (
            <button key={`${f.node_id}.${f.field}`} type="button"
              onClick={() => onPick(line(open.id, { what: 'step', node_id: f.node_id, field: f.field, stepValue: null }))} className={ROW}>
              <span className="truncate text-[color:var(--text-primary)]">{f.step} <span className="text-[color:var(--text-muted)]">›</span> <span className="font-mono">{f.field}</span></span>
              <span className="text-[11px] text-[color:var(--text-muted)]">{f.type === 'enum' ? 'choice' : f.type}</span>
            </button>
          ))}
        </>
      ) : null}
    </div>
  );
}
